'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { ExternalLink } from 'lucide-react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useGoogleMaps } from '@/hooks/useGoogleMaps';

/**
 * Reverse-geocoded addresses, keyed by coordinate.
 *
 * Module-level so a list of businesses doesn't bill a lookup per card, and so
 * hovering the same place twice is free. Coordinates are rounded to ~1m before
 * keying — the same pin re-serialised can differ in the last decimal.
 */
const addressCache = new Map<string, string>();

const cacheKey = (lat: number, lng: number) =>
  `${lat.toFixed(5)},${lng.toFixed(5)}`;

/**
 * Result types worth showing, most specific first.
 *
 * Google orders results by specificity but the first one is often a
 * `plus_code` — its grid reference, e.g. "2HC5P2G5+GR" — when the point
 * doesn't sit on a rooftop address. That's a valid location and useless to
 * read, so pick the most precise real address instead.
 */
const ADDRESS_TYPES = [
  'street_address',
  'premise',
  'subpremise',
  'establishment',
  'point_of_interest',
  'route',
  'neighborhood',
];

/** Google also prefixes some addresses with the plus code; drop it. */
const LEADING_PLUS_CODE =
  /^[23456789CFGHJMPQRVWX]{4,8}\+[23456789CFGHJMPQRVWX]{2,3},?\s*/;

function pickAddress(
  results: google.maps.GeocoderResult[] | null
): string | null {
  if (!results?.length) return null;

  const clean = (value?: string) => {
    const stripped = value?.replace(LEADING_PLUS_CODE, '').trim();
    return stripped ? stripped : null;
  };

  for (const type of ADDRESS_TYPES) {
    const match = results.find((r) => r.types?.includes(type));
    const address = clean(match?.formatted_address);
    if (address) return address;
  }

  // Nothing recognisable — anything is better than the grid reference.
  const fallback = results.find((r) => !r.types?.includes('plus_code'));
  return clean(fallback?.formatted_address);
}

function hasPin(
  coordinates?: [number, number] | null
): coordinates is [number, number] {
  return (
    Array.isArray(coordinates) &&
    coordinates.length === 2 &&
    coordinates.every((n) => typeof n === 'number' && Number.isFinite(n)) &&
    // (0, 0) is an unset coordinate, not a spot in the Gulf of Guinea.
    !(coordinates[0] === 0 && coordinates[1] === 0)
  );
}

/** A small, non-interactive map centred on the pin. */
function PinMap({ lat, lng, label }: { lat: number; lng: number; label: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ref.current || !window.google?.maps) return;
    const map = new window.google.maps.Map(ref.current, {
      center: { lat, lng },
      zoom: 15,
      disableDefaultUI: true,
      gestureHandling: 'none',
      keyboardShortcuts: false,
      clickableIcons: false,
    });
    new window.google.maps.Marker({ position: { lat, lng }, map, title: label });
  }, [lat, lng, label]);
  return (
    <div
      ref={ref}
      role="img"
      aria-label={`Map showing ${label}`}
      className="h-40 w-full rounded-lg bg-[#eae7e0]"
    />
  );
}

/**
 * Shows the exact street address — and a map — behind a neighborhood name.
 *
 * Only the pin's coordinates are stored — the form captures a formatted
 * address but never persists it — so the street address is resolved from them
 * on demand. Records without a pin render their children untouched, which is
 * most of them, so callers can wrap unconditionally.
 *
 * A popover, not a tooltip: it has a map in it, it must work on tap, and
 * people want to copy the address or jump to Google Maps for directions.
 */
export function NeighborhoodAddress({
  coordinates,
  children,
  triggerClassName,
}: {
  /** `[lng, lat]`, as every one of our APIs returns it. */
  coordinates?: [number, number] | null;
  children: React.ReactNode;
  triggerClassName?: string;
}) {
  const isLoaded = useGoogleMaps();
  const pinned = hasPin(coordinates);
  const [lng, lat] = pinned ? coordinates : [0, 0];
  const key = pinned ? cacheKey(lat, lng) : '';

  const [address, setAddress] = useState<string | null>(
    () => addressCache.get(key) ?? null
  );

  const resolve = useCallback(() => {
    if (!pinned || !isLoaded || address) return;
    const cached = addressCache.get(key);
    if (cached) {
      setAddress(cached);
      return;
    }
    new window.google.maps.Geocoder().geocode(
      { location: { lat, lng } },
      (results, status) => {
        if (status !== 'OK') return;
        const formatted = pickAddress(results);
        if (!formatted) return;
        addressCache.set(key, formatted);
        setAddress(formatted);
      }
    );
  }, [pinned, isLoaded, address, key, lat, lng]);

  // Resolved up front rather than on open: the tooltip is small and appears
  // instantly, so fetching on hover would show a coordinate pair that swaps to
  // an address a moment later.
  useEffect(() => {
    resolve();
  }, [resolve]);

  if (!pinned) return <div className={triggerClassName}>{children}</div>;

  const shown = address ?? `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  const mapsHref = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Show location on a map: ${shown}`}
          className={`cursor-pointer rounded-md text-left underline decoration-dotted underline-offset-4 hover:decoration-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black ${
            triggerClassName ?? ''
          }`}
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 space-y-3 p-3 font-[Merriweather]"
      >
        {isLoaded && <PinMap lat={lat} lng={lng} label={shown} />}
        <p className="text-sm text-black">{shown}</p>
        <a
          href={mapsHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#554E49] underline underline-offset-2 hover:text-black"
        >
          Open in Google Maps
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
      </PopoverContent>
    </Popover>
  );
}

export default NeighborhoodAddress;
